import { CircleDollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/SharedAuthContext';
import { FinopsOverviewTab } from '@/components/finops/FinopsOverviewTab';
import { FinopsAwsTab } from '@/components/finops/FinopsAwsTab';
import { FinopsGcpTab } from '@/components/finops/FinopsGcpTab';
import { FinopsVercelTab } from '@/components/finops/FinopsVercelTab';
import { EmptyState } from '@/components/finops/shared';

const TABS = ['overview', 'aws', 'gcp', 'vercel'] as const;

export function FinOpsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const denied: string[] = user?.deniedPermissions ?? [];

  const allowed = TABS.filter((id) => !denied.includes(`subtab:finops:${id}`));

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 shrink-0 mt-0.5">
          <CircleDollarSign className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('finops.page.title')}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {t('finops.page.description')}
          </p>
        </div>
      </div>

      {allowed.length === 0 ? (
        <EmptyState
          title={t('finops.no_access.title')}
          description={t('finops.no_access.description')}
        />
      ) : (
        <Tabs defaultValue={allowed[0]} className="space-y-4">
          <TabsList className="w-full justify-start overflow-x-auto">
            {allowed.includes('overview') && (
              <TabsTrigger value="overview">{t('finops.page.tab_overview')}</TabsTrigger>
            )}
            {allowed.includes('aws') && (
              <TabsTrigger value="aws">{t('finops.page.tab_aws')}</TabsTrigger>
            )}
            {allowed.includes('gcp') && (
              <TabsTrigger value="gcp">{t('finops.page.tab_gcp')}</TabsTrigger>
            )}
            {allowed.includes('vercel') && (
              <TabsTrigger value="vercel">{t('finops.page.tab_vercel')}</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview"><FinopsOverviewTab /></TabsContent>
          <TabsContent value="aws"><FinopsAwsTab /></TabsContent>
          <TabsContent value="gcp"><FinopsGcpTab /></TabsContent>
          <TabsContent value="vercel"><FinopsVercelTab /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
